import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {Icon} from '@Components/icon-v2';
import {Text, TextSize} from '@Components/text';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {isWindows} from 'react-device-detect';
import {AnimatePresence, motion} from 'framer-motion';
import {useAppSelector} from '@/hooks';
import styles from './poster-nav-opts.module.scss';
import {useIsVideoOrHasMultiPages} from '@Hooks/poster-editor/useIsVideoOrHasMultiPages';

interface PosterNavOptsProps {
  className?: string;
  visible?: boolean;
}

const SCALE_VAL_TIMEOUT = 3000;

export function PosterNavOpts({className = '', visible = true}: PosterNavOptsProps): ReactElement<any> | null {
  const scaling = useAppSelector((state) => {
    return state.posterEditor?.posterObject?.scaling;
  });
  const isSidebarOpen = useAppSelector((state) => {
    return state.posterEditor?.isSidebarOpen;
  });
  const isSidebarSmall = useAppSelector((state) => {
    return state.posterEditor?.isSidebarSmall;
  });
  const isVerticalScrollbarVisible =
    useAppSelector((state) => {
      return state.posterEditor?.isVerticalScrollbarVisible;
    }) || false;
  const isHorizontalScrollbarVisible =
    useAppSelector((state) => {
      return state.posterEditor.isHorizontalScrollbarVisible;
    }) || false;
  const scaleValTimeout = useRef<number | undefined>(undefined);
  const showFitToScreen = scaling && !scaling?.isCurrentScaleBestFit;
  const scale = scaling?.scale ?? 1;
  const [showScaleVal, setShowScaleVal] = useState(false);
  const isPosterVideo = useIsVideoOrHasMultiPages();
  const isTimelineModeActive = useAppSelector((state) => {
    return state.posterEditor.isTimelineModeActive;
  });
  const bottomBarHeight = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.bottomBarHeight;
  });

  let height = 78;

  useEffect(() => {
    if (!window.posterEditor?.whiteboard) {
      return;
    }
    setShowScaleVal(true);
    clearTimeout(scaleValTimeout.current);

    scaleValTimeout.current = window.setTimeout(() => {
      setShowScaleVal(false);
    }, SCALE_VAL_TIMEOUT);
  }, [scale]);

  if (showScaleVal) {
    height += 28;
  }

  if (showFitToScreen) {
    height += 36;
  }

  const fitToScreen = (): void => {
    window.posterEditor?.whiteboard?.scaling.fitToScreen();
  };

  const zoomOut = (): void => {
    window.posterEditor?.whiteboard?.scaling.zoomOut();
  };

  const zoomIn = (): void => {
    window.posterEditor?.whiteboard?.scaling.zoomIn();
  };

  const addExtraPaddingToRight = (): boolean => {
    return isWindows && isVerticalScrollbarVisible;
  };

  const addExtraPaddingToBottom = (): boolean => {
    return isWindows && isHorizontalScrollbarVisible;
  };

  const getBottomValue = (): number => {
    if (!isTimelineModeActive) {
      return 12;
    }

    return bottomBarHeight ? bottomBarHeight + 24 : 72;
  };

  return (
    <div
      style={{height: `${height}px`, bottom: `${getBottomValue()}px`}}
      className={`${className} spacing-p-1 radius-8 flexbox flex-align-items-end border-s-dark shadow-floating-elevation-2 _fit-width ${styles.zoomOpts} ${isPosterVideo ? styles.withBottomBar : ''} ${
        addExtraPaddingToRight() ? styles.withVerticalScrollBar : ''
      } ${addExtraPaddingToBottom() ? styles.withBottomScrollbar : ''} 
      ${isSidebarSmall && !isSidebarOpen && addExtraPaddingToRight() ? styles.collapsedWithVerticalScrollBar : ''}
      ${!isSidebarOpen && isSidebarSmall ? styles.smallCollapsed : ''} ${styles.bottomFloatingPosterOpts} ${visible ? styles.in : ''} js-hide`}
    >
      <div className={`${styles.icons} flexbox flex-column-reverse flex-items-center`}>
        <Icon icon="icon-zoom-out" onClick={zoomOut} shape={IconShape.SQUARE} type={IconType.TRANSPARENT} isUsedAsButton size={IconSize.SIZE_ICON_16} />
        <Icon icon="icon-zoom-in" onClick={zoomIn} shape={IconShape.SQUARE} type={IconType.TRANSPARENT} isUsedAsButton size={IconSize.SIZE_ICON_16} />
        {showFitToScreen ? (
          <Icon icon="icon-fit-to-screen" onClick={fitToScreen} shape={IconShape.SQUARE} type={IconType.TRANSPARENT} isUsedAsButton size={IconSize.SIZE_ICON_16} />
        ) : null}
        {showScaleVal ? <Text className="spacing-m-b-1" val={`${Math.round(scale * 100)}%`} size={TextSize.XXXSMALL} /> : null}
      </div>
    </div>
  );
}
